import React, { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { IDocumentPartProps } from "./IDocumentPartProps";

import {
  processTreeViewItems,
  TreeViewExpandChangeEvent,
  TreeViewItemClickEvent,
} from "@progress/kendo-react-treeview";
import "@progress/kendo-theme-bootstrap/dist/all.css";
import {
  fetchAllUserDocumentCheckout,
  fetchCheckDocumentIsCheckout,
  fetchCheckinDocument,
  fetchCheckoutDocument,
  fetchDocumentPartNodeWithPermissions,
  fetchDocumentPartStatus,
  fetchRecursiveParentsDocumentPart,
} from "../../api/document/DocumentsPart";
import { ContextualMenuItemType, IContextualMenuItem, IContextualMenuProps } from "@fluentui/react";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { DocumentPart } from "../../api/models";
import { WorkflowStatus } from "../../api/models/workflow-status";
import {
  applyFetchedChildren,
  copyItemsAndItem,
  fetchExpandedNodeExternal,
  fetchNodeChildren,
} from "./DocumentPartTreeHelper";
import { useWindowSize } from "../../utils/helpers/useWindowSizeHook";
import { CustomTreeView } from "./CustomTreeView";
import { UserApprover } from "../../api/models/user-approver";
import { DocumentPartWithSuperUser } from "../../api/models/document-part-with-super-user";
import { DocumentRoot } from "../../api/models/document-root";
import { DocumentPartWithStatus } from "../../api/models/document-part-with-status";
import { store } from "../../redux/store/store";
import {
  selectAllUserDocumentCheckout,
  selectUserDocumentCheckout,
  setAllUserDocumentCheckout,
  setUserDocumentCheckout,
} from "../../redux/feature/settings/configurations-slice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/hooks";
import { UserDocumentCheckout } from "../../api/models/user-document-checkout";
import {
  addInfoSection,
  addInfoTooltip,
  addLastUpdateHeaderItem,
  addMenuSection,
  addUserUpdateHeaderItem,
  checkApproveStatus,
  expandToNodeAndSelect,
  getLastModifiedDate,
  getWorkflowStatus,
} from "./DocumentPartTreeUtils";
import { UserLevelApprove } from "../../api/models/user-level-approve";
import { InlineActionsPanel } from "./InlineActionsPanel";
import { sortWorkflowActions } from "./workflowActions";
import { DocumentPermissionTO } from "../../api/models/document-permission-to";
import { useOutlineSearch } from "./search/useOutlineSearch";
import { OutlineSearchBar } from "./search/OutlineSearchBar";
import { OutlineSearchResults } from "./search/OutlineSearchResults";
import {
  searchCleared,
  selectionCleared,
  selectIsOutlineSearchResultsVisible,
  selectOutlineFilter,
} from "../../redux/feature/cpm/outline-search-slice";
import { scrollItemIntoCenter } from "./scrollUtils";
import { selectContentControlById } from "../../office/ContentControl";
import { getContentControlWithDocPartId } from "../../utils/helpers/ContentControlHelper";

const clampNumber = (num: number, a: number, b: number) => Math.max(Math.min(num, Math.max(a, b)), Math.min(a, b));

function createSkeletonItems(parentKey: string, count = 1): TreeViewDataItem[] {
  return Array.from({ length: count }, (_, i) => ({
    text: "",
    key: `__skeleton__-${parentKey}-${i}`,
    hasChildren: false,
    nodeType: "skeleton",
    data: {},
    expanded: false,
  }));
}

// IDS-5755: recursively collects the DocumentRoot payload (`.data`) of every visible "part"/"root"
// tree node whose id has NO entry at all in `documentPartWithStatus` yet ("pagination"/"skeleton"
// placeholders are never real parts and are skipped). A part that already has an entry - even a
// transient `workflowStatus: null` one set by changeState/restoreWorkflowStatus while a workflow
// action is in flight - counts as present and is intentionally left alone here, so this never
// interferes with that in-flight-action flow.
function collectPartsMissingStatus(
  items: TreeViewDataItem[] | undefined,
  documentPartWithStatus: DocumentPartWithStatus[] | undefined,
  result: DocumentRoot[]
): void {
  items?.forEach((treeItem) => {
    if (
      (treeItem.nodeType === "part" || treeItem.nodeType === "root") &&
      !documentPartWithStatus?.some((entry) => entry.documentPartId === treeItem.key)
    ) {
      result.push(treeItem.data);
    }
    collectPartsMissingStatus(treeItem.items, documentPartWithStatus, result);
  });
}

export const Tooltip = ({
  positionY,
  children,
}: {
  positionY: number;
  children: ReactElement | string | (ReactElement | string)[];
}) => {
  const { width, height } = useWindowSize();
  const positionMinusY = height - positionY;
  const bottomOffset = clampNumber(positionMinusY + 30, 5, height - 5);
  const topOffset = clampNumber(positionY + 30, 5, height - 5);
  const isInBottomScreen = positionY > height / 2;
  const topBottomStyles = isInBottomScreen ? { bottom: bottomOffset } : { top: topOffset };

  return (
    <div
      className="tooltip"
      style={{
        position: "fixed",
        backgroundColor: "var(--color-lightgrey)",
        color: "black",
        borderRadius: "5px",
        border: "1px solid var(--color-black)",
        padding: "2px",
        zIndex: 1000000000,
        opacity: 1,
        maxWidth: width - 10 + "px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        right: "50%",
        transform: "translateX(50%)",
        fontSize: "10px",
        ...topBottomStyles,
      }}
    >
      {children}
    </div>
  );
};

export interface DocumentPartTreeSelection {
  item: any;
  IdRoot: string;
  name: string;
  key: string;
  order: number;
  oidPath: string[];
  data: any;
}

export interface TreeState {
  rootId: string;
  treeItems: TreeViewDataItem[];
  selectState: string[];
  selectedId?: string;
  page: string;
}

export interface TreeViewDataItem {
  text: string;
  key: string;
  hasChildren: boolean;
  nodeType: "part" | "section" | "root" | "pagination" | "skeleton";
  expanded?: boolean;
  checked?: boolean;
  selected?: boolean;
  renaming?: boolean;
  items?: TreeViewDataItem[];
  data: any;
  driveItemId?: string;
  hasReadPermissions?: boolean;
  fatherMultilinked?: boolean;
  fatherKey?: string;
  multilinkedParents?: DocumentPart[];
}

export const DocumentPartTree: React.FunctionComponent<IDocumentPartProps> = (props) => {
  const { rootParts } = props;
  const expandIcons: boolean = props.expandIcons == undefined;
  const [state, setState] = useState<TreeState>({
    rootId: rootParts[0].key,
    treeItems: rootParts,
    selectState: [],
    page: "outline",
  });
  const [disableActionWorkflow, setDisableActionWorkflow] = useState<boolean>(false);
  const [userApprover, setUserApprover] = React.useState<Map<string, UserApprover>>(new Map());
  const [superUser, setSuperUser] = React.useState<Map<string, DocumentPartWithSuperUser>>(new Map());
  const userDocumentCheckouts: UserDocumentCheckout[] = useAppSelector(selectUserDocumentCheckout);
  const allUserDocumentCheckouts: UserDocumentCheckout[] = useAppSelector(selectAllUserDocumentCheckout);
  // Tracks node keys for which a children fetch is currently in-flight (used to cancel stale responses)
  const expandingNodes = useRef<Set<string>>(new Set());
  // Guards the [rootParts] reset effect: only resets state when the root document actually changes
  const prevRootIdRef = useRef<string | undefined>(rootParts[0]?.key);
  // Tracks the last treeItems echoed up via setTree, so we can ignore the resulting rootParts identity bounce.
  // Initialized to the same array seeded into state.treeItems so the first [rootParts] effect is a no-op.
  const lastEchoedTreeRef = useRef<TreeViewDataItem[] | undefined>(rootParts);

  // Guards: prevent setState on unmounted component and re-entrant handleSearchClear
  const mountedRef = useRef<boolean>(true);
  const clearInFlightRef = useRef<boolean>(false);

  const dispatch = useAppDispatch();
  const search = useOutlineSearch(state.rootId);
  // IDS-5755 (FE-7): filter state lives in the outline-search slice; the hook only exposes setFilter.
  const { assignedToMe, statusFilter } = useAppSelector(selectOutlineFilter);
  // IDS-5755 (FE-7): shared with DocumentProperties' own showSearchResults so both stay consistent.
  const showResults = useAppSelector(selectIsOutlineSearchResultsVisible);
  const resultsPanelRef = useRef<HTMLDivElement | null>(null);
  const treeWrapperRef = useRef<HTMLDivElement | null>(null);
  const [isRestoringFromSearch, setIsRestoringFromSearch] = useState<boolean>(false);
  // IDS-5755: remembers the previous showResults value so the retry effect below can detect a genuine
  // true -> false transition (filter/search results just got hidden, tree just became visible again)
  // instead of firing on every render or on the initial mount.
  const prevShowResultsRef = useRef<boolean>(showResults);
  const preloadedDirectChildrenDataRef = useRef<
    Map<string, { childIds: string[]; statuses: DocumentPartWithStatus[] }>
  >(new Map());

  // Dispatch selectionCleared on panel teardown; track mount state for async guards
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      dispatch(selectionCleared());
    };
  }, [dispatch]);

  // Helper: traverse treeItems to find the hierarchical index of a node by key
  const findHierarchicalIndex = useCallback(
    (items: TreeViewDataItem[], targetKey: string, prefix = ""): string | null => {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const idx = prefix ? `${prefix}_${i}` : `${i}`;
        if (item.key === targetKey) return idx;
        if (item.items?.length) {
          const found = findHierarchicalIndex(item.items, targetKey, idx);
          if (found) return found;
        }
      }
      return null;
    },
    []
  );

  // Handle X-clear: restore tree selection if a search result was selected (FR-012)
  const handleSearchClear = useCallback(async () => {
    const selectedDocPartId = search.selectedDocPartId;
    dispatch(searchCleared());

    // IDS-5755: the X button clears the text query only. If a status / "assigned to me"
    // filter is still active, keep the filter-only results visible instead of restoring the
    // full tree (searchCleared drops to idle, so re-fire the filter-only search here).
    if (assignedToMe || statusFilter !== null) {
      search.refireFilterOnly();
      return;
    }

    if (!selectedDocPartId) return;
    if (clearInFlightRef.current) return; // C3: re-entrancy guard
    clearInFlightRef.current = true;

    // C1: set flag AFTER early-returns, inside try/finally
    setIsRestoringFromSearch(true);
    try {
      const ancestors = await fetchRecursiveParentsDocumentPart(selectedDocPartId);
      const currentItems = await expandToNodeAndSelect(state.treeItems, ancestors ?? [], props.elementToUpdate);

      if (!mountedRef.current) return; // C2: unmount guard

      const targetHierarchicalIndex = findHierarchicalIndex(currentItems, selectedDocPartId);
      setState((s) => ({
        ...s,
        treeItems: currentItems,
        selectState: targetHierarchicalIndex ? [targetHierarchicalIndex] : s.selectState,
        selectedId: undefined,
      }));

      // Turn skeleton off BEFORE waiting for paint so the real tree
      // (with data-tree-item-id attributes) is in DOM when we poll/scroll.
      setIsRestoringFromSearch(false);

      if (!mountedRef.current) return;
      await scrollItemIntoCenter(treeWrapperRef.current, selectedDocPartId);
    } catch {
      if (mountedRef.current) dispatch(selectionCleared());
    } finally {
      if (mountedRef.current) setIsRestoringFromSearch(false);
      clearInFlightRef.current = false;
    }
  }, [
    search.selectedDocPartId,
    search.refireFilterOnly,
    assignedToMe,
    statusFilter,
    dispatch,
    state.treeItems,
    props.elementToUpdate,
    findHierarchicalIndex,
  ]);

  function certificationOptions(item: TreeViewDataItem) {
    let options: IContextualMenuItem[] = [];
    let items: IContextualMenuItem[] = [];
    let element: string[] = getWorkflowStatus(item.key, props.documentPartWithStatus)?.actions;
    let workflowStatus: WorkflowStatus = getWorkflowStatus(item.key, props.documentPartWithStatus);
    const inlineActions: string[] = disableActionWorkflow ? [] : sortWorkflowActions(element);

    const isCurrentNodeSignificant = Boolean(
      workflowStatus && (workflowStatus.isSignificant || workflowStatus.significant)
    );
    const lastUpdate =
      workflowStatus?.workflowStatusCode !== "20" &&
      workflowStatus?.workflowStatusCode !== "40" &&
      userApprover.get(item.key)?.lastUpdate
        ? getLastModifiedDate(userApprover.get(item.key)?.lastUpdate)
        : null;
    const infoItems = [];

    const directChildrenIds = Array.from(
      new Set(
        [
          ...(item.items ?? [])
            .filter((child) => child.nodeType === "part" || child.nodeType === "root")
            .map((child) => child.key),
          ...((item.data?.subparts ?? item.data?.documentPart?.subparts ?? []) as any[])
            .map((child) => child?.documentPart?.id ?? child?.id)
            .filter((id): id is string => Boolean(id)),
        ].filter((id) => id !== item.key)
      )
    );
    const hasActionableBulkOperation = directChildrenIds.some((childId) => {
      const childWorkflowStatus = getWorkflowStatus(childId, props.documentPartWithStatus);
      const isCheckedOut = allUserDocumentCheckouts?.some((checkout) => checkout.documentId === childId);
      const isSignificant = Boolean(
        childWorkflowStatus && (childWorkflowStatus.isSignificant || childWorkflowStatus.significant)
      );
      if (isCheckedOut || !isSignificant || !childWorkflowStatus?.actions?.length) {
        return false;
      }

      return childWorkflowStatus.actions.some(
        (action) =>
          action === "SUBMIT" ||
          action === "APPROVE" ||
          action === "APPROVE2" ||
          action === "APPROVE3" ||
          action === "APPROVE4" ||
          action === "APPROVE5"
      );
    });

    const shouldHideInfoForNonSignificantParent =
      !isCurrentNodeSignificant && inlineActions.length === 0 && hasActionableBulkOperation;
    const hasPotentialDirectChildren =
      item.hasChildren || Boolean(item.data?.hasChildren || item.data?.documentPart?.hasChildren);
    const preloadedDirectChildrenData = preloadedDirectChildrenDataRef.current.get(item.key);

    const hasActionablePreloadedChildren =
      preloadedDirectChildrenData != null &&
      (preloadedDirectChildrenData.statuses?.some((statusEntry) => {
        const ws = statusEntry.workflowStatus;
        if (!ws?.actions?.length) return false;
        const isSignificant = Boolean(ws.isSignificant || (ws as any).significant);
        const isCheckedOut = allUserDocumentCheckouts?.some((c) => c.documentId === statusEntry.documentPartId);
        if (!isSignificant || isCheckedOut) return false;
        return ws.actions.some(
          (a) =>
            a === "SUBMIT" ||
            a === "APPROVE" ||
            a === "APPROVE2" ||
            a === "APPROVE3" ||
            a === "APPROVE4" ||
            a === "APPROVE5"
        );
      }) ??
        false);

    const hasPotentialChildrenToShow =
      hasPotentialDirectChildren && (preloadedDirectChildrenData == null || hasActionablePreloadedChildren);

    if (!shouldHideInfoForNonSignificantParent) {
      if (inlineActions.length > 0) {
        let userUpdInfo = getUserUpdInfo(workflowStatus, item.key);
        if (userUpdInfo !== "") {
          addUserUpdateHeaderItem(infoItems, userUpdInfo);
        }
      }
      if (lastUpdate) {
        addLastUpdateHeaderItem(infoItems, lastUpdate);
      }
    }

    if (inlineActions.length > 0 || hasActionableBulkOperation || hasPotentialChildrenToShow) {
      items.push({
        key: "inlineActions",
        itemType: ContextualMenuItemType.Normal,
        onRender: () => (
          <InlineActionsPanel
            inlineActions={inlineActions}
            allUserDocumentCheckouts={allUserDocumentCheckouts}
            userDocumentCheckouts={userDocumentCheckouts}
            superUser={superUser}
            workflowStatus={workflowStatus}
            item={item}
            rootId={state.rootId}
            setCheckoutDocument={setCheckoutDocument}
            setCheckinDocument={setCheckinDocument}
            setDisableActionWorkflow={setDisableActionWorkflow}
            documentPartProps={props}
            preloadedDirectChildrenIds={preloadedDirectChildrenData?.childIds}
            preloadedDirectChildrenStatuses={preloadedDirectChildrenData?.statuses}
          />
        ),
      });
    }

    if (items?.length) {
      addInfoSection(options, infoItems);
      addMenuSection(options, items);
    } else if (checkApproveStatus(workflowStatus)) {
      manageOtherApprover(infoItems, options, item.key, workflowStatus?.workflowStatusCode);
    }
    return {
      items: options,
      directionalHintFixed: true,
    };
  }

  function getUserUpdInfo(workflowStatus: WorkflowStatus, key: string) {
    let { map, splitName } = getCorrectMap(key);
    switch (workflowStatus?.workflowStatusCode) {
      case "00":
      case "10":
      case "20":
      case "40":
        if (map?.size === 1) {
          return formatMessage("cof.base.submit.to") + " " + splitName;
        } else if (map?.size > 1) {
          let userApproves = Array.from(map.values())
            .map((v) => v.levelApprove)
            .sort((a, b) => a - b);
          if (userApproves[0] !== userApproves[1]) {
            return formatMessage("cof.base.submit.to") + " " + splitName;
          } else {
            return formatMessage("cof.base.submit.to.multiple.user");
          }
        } else {
          return "";
        }
      case "30":
      case "31":
      case "32":
      case "33":
      case "34":
      case "50":
        return (
          formatMessage("cof.base.submitted.by") +
          " " +
          (workflowStatus?.displayUserName ?? workflowStatus?.userUpd)?.split("@")[0]
        );
      default:
        return "";
    }
  }

  function manageOtherApprover(
    infoItems: any[],
    options: IContextualMenuItem[],
    key: string,
    workflowStatusCode: string
  ) {
    let { map, splitName } = getCorrectMap(key);
    let myMap = new Map<string, number>();
    map.forEach((level) => {
      myMap.set(level.displayName, Number(level.levelApprove));
    });

    addInfoTooltip(myMap, splitName, workflowStatusCode, options);
    addInfoSection(options, infoItems);
  }

  function getCorrectMap(key: string) {
    let map = new Map<string, UserLevelApprove>();
    let splitName: string = "";
    if (userApprover.get(key)?.userApproverMap) {
      map = new Map(
        Object.entries(userApprover.get(key)?.userApproverMap || {}).sort(
          (a, b) => a[1].levelApprove - b[1].levelApprove
        )
      );
      splitName = map?.values()?.next()?.value?.displayName;
    }
    return { map, splitName };
  }

  useEffect(() => {
    fetchAllUserDocumentCheckout().then((allUserCheckoutDocument) => {
      store.dispatch(setAllUserDocumentCheckout(allUserCheckoutDocument));
    });
  }, [userDocumentCheckouts]);

  function setCheckoutDocument(item: TreeViewDataItem) {
    let driveItemId =
      item?.data?.documentPart === undefined ? item?.data?.driveItemId : item?.data?.documentPart?.driveItemId;
    let siteId = item?.data?.documentPart === undefined ? item?.data?.siteId : item?.data?.documentPart?.siteId;
    fetchCheckoutDocument(siteId, driveItemId, item?.key).then((result) => {
      store.dispatch(setUserDocumentCheckout(result));
      openDocOnDoubleClick(item);
    });
  }

  function setCheckinDocument(item: TreeViewDataItem) {
    let driveItemId =
      item?.data?.documentPart === undefined ? item?.data?.driveItemId : item?.data?.documentPart?.driveItemId;
    let siteId = item?.data?.documentPart === undefined ? item?.data?.siteId : item?.data?.documentPart?.siteId;
    fetchCheckinDocument(siteId, driveItemId, item?.key).then(() => {
      store.dispatch(setUserDocumentCheckout([]));
    });
  }

  useEffect(() => {
    const nextRootId = rootParts[0]?.key;
    const rootChanged = nextRootId !== prevRootIdRef.current;
    // Ignore the rootParts reference bounce caused by our own setTree echo.
    if (!rootChanged && rootParts === lastEchoedTreeRef.current) {
      return;
    }
    prevRootIdRef.current = nextRootId;
    if (rootChanged) {
      setState({ rootId: nextRootId, treeItems: rootParts, selectState: [], page: "outline" });
    } else {
      // External update from the parent (e.g. rename flag): sync treeItems without resetting selection.
      setState((s) => ({ ...s, treeItems: rootParts }));
    }
  }, [rootParts]);

  useEffect(() => {
    lastEchoedTreeRef.current = state.treeItems;
    props.setTree?.(state.treeItems);
  }, [state.treeItems]);

  // IDS-5755: the tree is only ever hidden with display:none while search/filter results are shown -
  // it is never unmounted - and DocumentOutline's background status-loading effect only re-fires when
  // its `elementToUpdate` state changes. So a part whose status fetch was still in flight when the user
  // applied the outline status filter never gets refetched once the filter is cleared and the tree
  // reappears, leaving its spinner stuck forever. On the true -> false transition of `showResults`,
  // retry the fetch for exactly the visible parts that are still missing a status entry.
  useEffect(() => {
    const wasShowingResults = prevShowResultsRef.current;
    prevShowResultsRef.current = showResults;
    if (!wasShowingResults || showResults) return;

    const missingStatusParts: DocumentRoot[] = [];
    collectPartsMissingStatus(state.treeItems, props.documentPartWithStatus, missingStatusParts);
    if (missingStatusParts.length) {
      props.elementToUpdate?.(missingStatusParts);
    }
  }, [showResults]);

  const treePath = (items: TreeViewDataItem[], indexes: number[]): TreeViewDataItem[] => {
    const item = items[indexes[0]];
    if (item?.items?.length) return [item, ...treePath(item.items, indexes.slice(1))];
    return [item];
  };

  const onExpandChange = (event: TreeViewExpandChangeEvent) => {
    if (isRestoringFromSearch) return;
    const indexes = event.itemHierarchicalIndex.split("_").map(Number);

    // Navigate to the item at the given hierarchical path
    let current: TreeViewDataItem | undefined = state.treeItems[indexes[0]];
    for (let i = 1; i < indexes.length; i++) {
      current = current?.items?.[indexes[i]];
    }
    if (!current) {
      return;
    }

    const nodeKey = current.key;
    const isCurrentlyExpanded = current.expanded === true;

    // Collapsing a node whose children are being fetched — cancel the in-flight request
    if (isCurrentlyExpanded && expandingNodes.current.has(nodeKey)) {
      expandingNodes.current.delete(nodeKey);
      const { items: collapsedItems, item: collapsedNode } = copyItemsAndItem(state.treeItems, indexes);
      collapsedNode.expanded = false;
      collapsedNode.items = undefined;
      setState((s) => ({ ...s, treeItems: collapsedItems }));
      return;
    }

    // If the node only contains skeleton placeholders, data is still loading: ignore toggles.
    // This prevents first expansion from collapsing/short-circuiting before real children arrive.
    if (current.items?.length && current.items.every((treeItem) => treeItem.nodeType === "skeleton")) {
      return;
    }

    // Children already cached — expand/collapse instantly without a network call
    if (current.items !== undefined) {
      setState((s) => {
        const { items: toggledItems, item: toggledItem } = copyItemsAndItem(s.treeItems, indexes);
        if (!toggledItem) return s;
        toggledItem.expanded = !toggledItem.expanded;
        return { ...s, treeItems: toggledItems };
      });
      return;
    }

    // First expansion: show skeleton children immediately, then load real data
    const skeletonItems = createSkeletonItems(nodeKey);

    const { items: skeletonTree, item: skeletonNode } = copyItemsAndItem(state.treeItems, indexes);
    skeletonNode.expanded = true;
    skeletonNode.items = skeletonItems;
    setState((s) => ({ ...s, treeItems: skeletonTree }));

    expandingNodes.current.add(nodeKey);

    // The fetched children are merged into the *current* tree (functional setState) instead of into
    // a snapshot taken when the expansion started. Expanding two nodes in quick succession used to
    // make the slower response overwrite the tree with a stale snapshot, leaving the other node
    // stuck on its skeleton forever.
    fetchNodeChildren(nodeKey, 0, event.itemHierarchicalIndex === "0", state.treeItems[0]?.data, (value) =>
      props.elementToUpdate?.(value)
    )
      .then(({ dataPart, childrenDocumentPart }) => {
        if (expandingNodes.current.has(nodeKey)) {
          setState((s) => ({
            ...s,
            treeItems: applyFetchedChildren(s.treeItems, indexes, 0, dataPart, childrenDocumentPart),
          }));
        }
      })
      .finally(() => {
        expandingNodes.current.delete(nodeKey);
      });
  };

  const fetchExpandedNode = useCallback(
    (id: string, isReload: boolean, page: number) => {
      const snapshotItems = state.treeItems;

      if (page > 0) {
        const paginationIndexes = id.split("_").map(Number);
        const parentIndexes = paginationIndexes.slice(0, -1);
        if (parentIndexes.length > 0) {
          const { items: loadingItems, item: parentItem } = copyItemsAndItem(snapshotItems, parentIndexes);
          parentItem.expanded = true;
          parentItem.items = [
            ...(parentItem.items ?? []).filter((treeItem) => treeItem.nodeType !== "pagination"),
            ...createSkeletonItems(parentItem.key),
          ];
          setState((s) => ({ ...s, treeItems: loadingItems }));
        }
      }

      fetchExpandedNodeExternal(id, isReload, page, false, snapshotItems, props.elementToUpdate)
        .then((treeItems) => setState((s) => ({ ...s, treeItems })))
        .catch(() => {
          if (page > 0) {
            setState((s) => ({ ...s, treeItems: snapshotItems }));
          }
        });
    },
    [props.elementToUpdate, state.treeItems]
  );

  const selectItem = (event: TreeViewItemClickEvent) => {
    if (
      event.item.nodeType !== "section" &&
      event.item.nodeType !== "pagination" &&
      event.item.nodeType !== "skeleton"
    ) {
      // If search had a selected result and user selects a different tree node, clear search selection
      if (search.selectedDocPartId && search.selectedDocPartId !== event.item.key) {
        dispatch(selectionCleared());
      }
      setState((s) => ({
        ...s,
        selectState: [event.itemHierarchicalIndex],
        selectedId: event.item.key,
      }));
      // When the outline is reached via the Outline ribbon (not via Library) and the user clicks
      // on a direct child of the currently opened part, highlight the matching content control in
      // Word. The "opened part" is the root if no part is opened, or any sub-part the user drilled
      // into via the library. props.selectedPartId carries that key.
      const clickIndexes = event.itemHierarchicalIndex.split("_").map((value) => Number(value));
      let clickParent: TreeViewDataItem | undefined;
      let clickCurrent: TreeViewDataItem | undefined = state.treeItems[clickIndexes[0]];
      for (let i = 1; i < clickIndexes.length && clickCurrent; i++) {
        const next = clickCurrent.items?.[clickIndexes[i]];
        if (!next) {
          clickParent = undefined;
          clickCurrent = undefined;
          break;
        }
        clickParent = clickCurrent;
        clickCurrent = next;
      }
      if (
        props.isOutlineRibbon &&
        !props.isLibraryRibbon &&
        props.selectedPartId &&
        clickParent?.key === props.selectedPartId &&
        clickCurrent != null
      ) {
        const ccId = (event.item.data as { documentPart?: { contentControlId?: number } } | undefined)?.documentPart
          ?.contentControlId;
        if (ccId != null) {
          selectContentControlById(ccId).catch(() => undefined);
        } else {
          const docPartId = event.item.key;
          getContentControlWithDocPartId(docPartId)
            .then((coObject) => {
              const resolvedId = coObject?.contentControlId;
              if (resolvedId != null) {
                return selectContentControlById(Number(resolvedId));
              }
              return undefined;
            })
            .catch(() => undefined);
        }
      }
      if (props.onItemClick) {
        props.onItemClick({
          item: event.item,
          IdRoot: event.item.IdRoot,
          name: event.item.data.name,
          order: event.item.order,
          key: event.item.key,
          data: event.item,
          oidPath: treePath(
            state.treeItems,
            event.itemHierarchicalIndex.split("_").map((value) => Number(value))
          ).map((item) => (item ? item.key : "0")),
        });
      }
    }
  };

  const onItemClick = (event: TreeViewItemClickEvent) => {
    if (isRestoringFromSearch) return;
    event.nativeEvent.stopPropagation();
    event.nativeEvent.preventDefault();
    if (event.item.renaming) {
      return;
    }
    switch (event.syntheticEvent["detail"]) {
      case 1: // single click
        selectItem(event);
        break;
      case 2: // double click
        openDocOnDoubleClick(event.item);
        break;
    }
  };

  function openDocOnDoubleClick(item: TreeViewDataItem) {
    let isCurrentDocumentCheckoutForAnyUsers: boolean =
      Array.isArray(allUserDocumentCheckouts) && allUserDocumentCheckouts.some((udc) => udc.documentId === item.key);
    let siteId = item?.data?.documentPart === undefined ? item?.data?.siteId : item?.data?.documentPart?.siteId;
    let driveItemId =
      item?.data?.documentPart === undefined ? item?.data?.driveItemId : item?.data?.documentPart?.driveItemId;
    if (item.hasReadPermissions !== false && item.nodeType !== "pagination") {
      if (isCurrentDocumentCheckoutForAnyUsers) {
        fetchCheckDocumentIsCheckout(siteId, driveItemId, item.key).then((isDocumentStillCheckout) => {
          if (!isDocumentStillCheckout) {
            store.dispatch(setUserDocumentCheckout([]));
          }
        });
      }
      props.openDocument(item.key);
    }
  }

  const onFetchComplete = (key: string, userApprover: UserApprover) => {
    setUserApprover((m) => {
      m.set(key, userApprover);
      return m;
    });
  };

  const onFetchCompleteForSuperUser = (key: string, documentPartWithSuperUser: DocumentPartWithSuperUser) => {
    setSuperUser((m) => {
      m.set(key, documentPartWithSuperUser);
      return m;
    });
  };

  const documentPartWithStatusRef = useRef(props.documentPartWithStatus);
  useEffect(() => {
    documentPartWithStatusRef.current = props.documentPartWithStatus;
  }, [props.documentPartWithStatus]);

  const onMenuWillOpen = useCallback(
    async (menuItem: TreeViewDataItem) => {
      let childIds: string[] = [
        ...(menuItem.items ?? []).filter((c) => c.nodeType === "part" || c.nodeType === "root").map((c) => c.key),
        ...((menuItem.data?.subparts ?? menuItem.data?.documentPart?.subparts ?? []) as any[])
          .map((c) => c?.documentPart?.id ?? c?.id)
          .filter(Boolean),
      ].filter((id) => id !== menuItem.key);
      childIds = Array.from(new Set(childIds));

      const mightHaveChildren =
        childIds.length > 0 ||
        menuItem.hasChildren ||
        menuItem.data?.hasChildren ||
        menuItem.data?.documentPart?.hasChildren;

      if (!mightHaveChildren) return;

      if (childIds.length === 0) {
        let page = 0;
        let totalPages = 1;
        while (page < totalPages) {
          const response = await fetchDocumentPartNodeWithPermissions(menuItem.key, page, {
            params: { notShowLoading: true },
          });
          const currentIds = (response?._embedded?.content ?? [])
            .map((c: any) => c?.documentPart?.id)
            .filter((id: string | undefined): id is string => Boolean(id));
          childIds.push(...currentIds);
          totalPages = Math.max(response?.page?.totalPages ?? 0, 1);
          page += 1;
        }
        childIds = Array.from(new Set(childIds));
      }

      if (childIds.length === 0) return;

      const current = documentPartWithStatusRef.current ?? [];
      const missingIds = childIds.filter((id) => !current.some((statusItem) => statusItem.documentPartId === id));
      const knownStatuses = current.filter((statusItem) => childIds.includes(statusItem.documentPartId));
      const fetchedStatuses = missingIds.length ? await fetchDocumentPartStatus(state.rootId, missingIds) : [];
      const mergedStatusesMap = new Map<string, DocumentPartWithStatus>();

      knownStatuses.forEach((statusItem) => mergedStatusesMap.set(statusItem.documentPartId, statusItem));
      (fetchedStatuses ?? []).forEach((statusItem) => mergedStatusesMap.set(statusItem.documentPartId, statusItem));

      preloadedDirectChildrenDataRef.current.set(menuItem.key, {
        childIds,
        statuses: Array.from(mergedStatusesMap.values()),
      });
    },
    [state.rootId]
  );

  // Factory: builds IContextualMenuProps for a search-result row's pallino click
  function getWorkflowStatusMenuProps(row: DocumentPermissionTO): IContextualMenuProps {
    if (!search.scopeRootId) {
      return { items: [], directionalHintFixed: true };
    }
    const workflowStatus = row.workflowStatus;
    if (!workflowStatus) {
      return { items: [], directionalHintFixed: true };
    }
    const resolvedScopeRootId = search.scopeRootId;
    const minimalItem: TreeViewDataItem = {
      text: row.documentPart.name,
      key: row.documentPart.id,
      hasChildren: false,
      nodeType: "part",
      data: {},
      hasReadPermissions: row.hasReadPermissions,
    };
    const inlineActions: string[] = disableActionWorkflow ? [] : sortWorkflowActions(workflowStatus.actions);
    const options: IContextualMenuItem[] = [];
    if (inlineActions.length > 0) {
      const menuItems: IContextualMenuItem[] = [
        {
          key: "inlineActions",
          itemType: ContextualMenuItemType.Normal,
          onRender: () => (
            <InlineActionsPanel
              inlineActions={inlineActions}
              allUserDocumentCheckouts={allUserDocumentCheckouts}
              userDocumentCheckouts={userDocumentCheckouts}
              superUser={superUser}
              workflowStatus={workflowStatus}
              item={minimalItem}
              rootId={resolvedScopeRootId}
              setCheckoutDocument={setCheckoutDocument}
              setCheckinDocument={setCheckinDocument}
              setDisableActionWorkflow={setDisableActionWorkflow}
              documentPartProps={props}
            />
          ),
        },
      ];
      addMenuSection(options, menuItems);
    }
    return { items: options, directionalHintFixed: true };
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {!props.isSourcesRibbon && (
        <div className="outline-search-sticky" style={{ flexShrink: 0 }}>
          <OutlineSearchBar
            value={search.query}
            onChange={search.setQuery}
            onClear={handleSearchClear}
            onSubmit={search.flushSearch}
            enableFilter={props.enableFilter}
            filter={{ assignedToMe, statusFilter }}
            onToggleAssignedToMe={() => search.setFilter({ assignedToMe: !assignedToMe, statusFilter })}
            onSelectStatus={(category) => search.setFilter({ assignedToMe, statusFilter: category })}
          />
        </div>
      )}
      <div
        ref={resultsPanelRef}
        data-testid="outline-scroll-container"
        style={{
          display: showResults ? "flex" : "none",
          flexDirection: "column",
          width: "100%",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        <OutlineSearchResults
          search={search}
          openedDocRootId={state.rootId}
          scrollContainerRef={resultsPanelRef}
          onOpenDocPart={(docPartId) => {
            const minimalItem: TreeViewDataItem = {
              text: "",
              key: docPartId,
              hasChildren: false,
              nodeType: "part",
              data: {},
              hasReadPermissions: true,
            };
            openDocOnDoubleClick(minimalItem);
          }}
          getWorkflowStatusMenuProps={getWorkflowStatusMenuProps}
        />
      </div>
      <div
        ref={treeWrapperRef}
        style={{
          display: showResults ? "none" : "flex",
          flexDirection: "column",
          width: "100%",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        <CustomTreeView
          data={
            isRestoringFromSearch
              ? processTreeViewItems(createSkeletonItems(state.treeItems[0]?.key ?? "root"), { select: [] })
              : processTreeViewItems(state.treeItems, { select: state.selectState })
          }
          expandIcons={expandIcons}
          onExpandChange={onExpandChange}
          selectedItemId={props.selectedPartId}
          treeItemRowProps={{
            onDocumentNameChange: props.onDocumentNameChange,
            isDraggable: state.page === "dragedrop",
            getIconButtonMenuProps: props.getIconButtonMenuProps,
            fetchExpandedNode: fetchExpandedNode,
            isLinkExisting: props.isLinkExisting,
            certificationOptions: certificationOptions,
            azureDefaultSite: props.azureDefaultSite,
            isOutlineRibbon: props.isOutlineRibbon,
            isOpenOutlineRibbon: props.isOpenOutlineRibbon,
            documentPartWithStatus: props.documentPartWithStatus,
            onFetchComplete: onFetchComplete,
            onFetchCompleteForSuperUser: onFetchCompleteForSuperUser,
            onMenuWillOpen: onMenuWillOpen,
          }}
          onItemClick={onItemClick}
          size={"small"}
        />
      </div>
    </div>
  );
};
